import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import type { DeleteEntityTextElements } from "~/components/DeleteEntityView";
import DeleteEntityView from "~/components/DeleteEntityView";
import { deleteOrder } from "~/modules/project/api/order/orderApiDispatchers.ts";
import { PROJECTS_ORDERS_QUERY_KEY } from "~/modules/project/api/order/orderQueries.ts";

type DeleteOrderModalProps = {
	isOpen: boolean;
	orderId: string;
	orderTitle: string;
	projectId: string;
	onCloseClick: () => void;
};

const DeleteOrderModal: React.FunctionComponent<DeleteOrderModalProps> = ({
	isOpen,
	orderId,
	orderTitle,
	projectId,
	onCloseClick,
}) => {
	const [busy, setBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [orderIsNotDeletable, setOrderIsNotDeletable] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const handleConfirmationClick = async () => {
		if (orderIsNotDeletable) return;
		try {
			setBusy(true);
			await deleteOrder({ orderId, projectId });
			await queryClient.invalidateQueries({ queryKey: PROJECTS_ORDERS_QUERY_KEY(projectId) });
			handleClose();
		} catch (error) {
			const errorData = handleApiError(error);
			if (errorData.type === ApiResponseErrorType.VALIDATION) {
				if (
					errorData.messages.orderId.find(
						(validationError) => validationError.rule === "orderIsDeletableRule",
					)
				) {
					setOrderIsNotDeletable(true);
					setServerErrorMessage("Die Bestellung kann nicht gelöscht werden.");
				}
			} else {
				setServerErrorMessage("Ein unbekannter Fehler ist aufgetreten.");
			}
		}
		setBusy(false);
	};

	const handleClose = useCallback(() => {
		setBusy(false);
		setServerErrorMessage("");
		setOrderIsNotDeletable(false);
		onCloseClick();
	}, [onCloseClick]);

	const texts: DeleteEntityTextElements = {
		headerText: `Bestellung "${orderTitle}" löschen`,
		bodyText: `Möchtest Du diese Bestellung wirklich löschen?`,
		deleteButtonText: `Bestellung löschen`,
	};

	return (
		<DeleteEntityView
			isOpen={isOpen}
			error={serverErrorMessage}
			texts={texts}
			onCloseClick={handleClose}
			busy={busy}
			handleConfirmationClick={handleConfirmationClick}
		>
			{serverErrorMessage && <div className="text-sm">
				<p>Die Bestellung kann nicht gelöscht werden. Mögliche Gründe dafür sind:</p>
				<ul className="my-4 ml-4 list-disc text-sm">
					<li>Der <strong>Status</strong> der Bestellung ist "bestätigt".</li>
					<li>Es wurden bereits <strong>Rechnungen</strong> auf Basis dieser Bestellung erstellt.</li>
					<li>Es wurden bereits <strong>Erfassungen</strong> auf diese Bestellung getätigt.</li>
				</ul>
			</div>}

		</DeleteEntityView>
	);
};

export default DeleteOrderModal;
